function extractDatePartLocal(value: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const tPart = value.split('T')[0];
  if (tPart && /^\d{4}-\d{2}-\d{2}$/.test(tPart)) return tPart;
  const spacePart = value.split(' ')[0];
  return spacePart && /^\d{4}-\d{2}-\d{2}$/.test(spacePart) ? spacePart : '';
}

export const DEFAULT_COMPANY_DATE_FORMAT = 'dd/mm/yyyy';
export const COMPANY_DATE_FORMAT_STORAGE_KEY = 'staffly:companyDateFormat';

export type CompanyDateFormatId = 'dd/mm/yyyy' | 'mm/dd/yyyy' | 'yyyy-mm-dd' | 'dd-mm-yyyy';

const LISTENERS = new Set<() => void>();

function readStoredFormat(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return localStorage.getItem(COMPANY_DATE_FORMAT_STORAGE_KEY);
  } catch {
    return null;
  }
}

let currentFormat: CompanyDateFormatId = normalizeCompanyDateFormat(readStoredFormat());

export function normalizeCompanyDateFormat(value?: string | null): CompanyDateFormatId {
  const v = (value || '').trim().toLowerCase().replace(/m/g, 'm');
  if (v === 'mm/dd/yyyy') return 'mm/dd/yyyy';
  if (v === 'yyyy-mm-dd') return 'yyyy-mm-dd';
  if (v === 'dd-mm-yyyy') return 'dd-mm-yyyy';
  if (v === 'dd/mm/yyyy') return 'dd/mm/yyyy';
  return DEFAULT_COMPANY_DATE_FORMAT;
}

export function getCompanyDateFormat(): CompanyDateFormatId {
  return currentFormat;
}

/** Locale for month/weekday names that matches the company numeric order. */
export function getCompanyDateLocale(format: CompanyDateFormatId = currentFormat): string {
  return format === 'mm/dd/yyyy' ? 'en-US' : 'en-IE';
}

export function applyCompanyDateFormat(value?: string | null): CompanyDateFormatId {
  const next = normalizeCompanyDateFormat(value);
  if (next === currentFormat) return next;
  currentFormat = next;
  if (typeof window !== 'undefined') {
    try {
      localStorage.setItem(COMPANY_DATE_FORMAT_STORAGE_KEY, next);
    } catch {
      /* ignore quota */
    }
  }
  LISTENERS.forEach((fn) => fn());
  return next;
}

export function resetCompanyDateFormat(): void {
  currentFormat = DEFAULT_COMPANY_DATE_FORMAT;
  if (typeof window !== 'undefined') {
    try {
      localStorage.removeItem(COMPANY_DATE_FORMAT_STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }
  LISTENERS.forEach((fn) => fn());
}

export function subscribeCompanyDateFormat(listener: () => void): () => void {
  LISTENERS.add(listener);
  return () => LISTENERS.delete(listener);
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function toCalendarParts(value: Date | string | number | null | undefined): { y: number; m: number; d: number } | null {
  if (value == null || value === '') return null;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return { y: value.getFullYear(), m: value.getMonth() + 1, d: value.getDate() };
  }
  if (typeof value === 'number') {
    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) return null;
    return { y: dt.getFullYear(), m: dt.getMonth() + 1, d: dt.getDate() };
  }
  const datePart = extractDatePartLocal(String(value));
  if (datePart) {
    const [y, m, d] = datePart.split('-').map(Number);
    if (!y || !m || !d) return null;
    return { y, m, d };
  }
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return null;
  return { y: dt.getFullYear(), m: dt.getMonth() + 1, d: dt.getDate() };
}

function toDate(value: Date | string | number | null | undefined): Date | null {
  if (value == null || value === '') return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const parts = toCalendarParts(value);
  if (!parts) return null;
  return new Date(parts.y, parts.m - 1, parts.d);
}

/** Numeric date using Company Settings → Date Format. Default dd/mm/yyyy. */
export function formatCompanyDate(
  value: Date | string | number | null | undefined,
  format: CompanyDateFormatId = currentFormat
): string {
  const parts = toCalendarParts(value);
  if (!parts) return '';
  const dd = pad2(parts.d);
  const mm = pad2(parts.m);
  const yyyy = String(parts.y);
  switch (format) {
    case 'mm/dd/yyyy':
      return `${mm}/${dd}/${yyyy}`;
    case 'yyyy-mm-dd':
      return `${yyyy}-${mm}-${dd}`;
    case 'dd-mm-yyyy':
      return `${dd}-${mm}-${yyyy}`;
    default:
      return `${dd}/${mm}/${yyyy}`;
  }
}

/** Date + HH:mm using the company date format (local time for instants). */
export function formatCompanyDateTime(value: Date | string | number | null | undefined): string {
  if (value == null || value === '') return '';
  const dt = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(dt.getTime())) return '';
  const date = formatCompanyDate(dt);
  if (!date) return '';
  return `${date} ${pad2(dt.getHours())}:${pad2(dt.getMinutes())}`;
}

export function formatCompanyDateRange(
  start: Date | string | number | null | undefined,
  end: Date | string | number | null | undefined
): string {
  const a = formatCompanyDate(start);
  const b = formatCompanyDate(end);
  if (!a) return b;
  if (!b || a === b) return a;
  return `${a} – ${b}`;
}

/** Named month/weekday display using the locale that matches company date order. */
export function formatCompanyDateLocale(
  value: Date | string | number | null | undefined,
  options: Intl.DateTimeFormatOptions
): string {
  const dt = toDate(value);
  if (!dt) return '';
  return dt.toLocaleDateString(getCompanyDateLocale(), options);
}
